// src/components/nav/AppTabs.tsx
// The signed-in navigation: the same tabs as a bottom bar on mobile and inside the top bar on
// desktop. One list so the Admin tab's visibility rule lives in one place.
import { Link, useLocation } from "react-router-dom";
import { BarChart2, House, Shield, UserRound, type LucideIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

export interface AppTab { key: "home" | "stats" | "profile" | "admin"; label: string; to: string; icon: LucideIcon }

const BASE_TABS: AppTab[] = [
  { key: "home", label: "Home", to: "/home", icon: House },
  { key: "stats", label: "Stats", to: "/home/stats", icon: BarChart2 },
  { key: "profile", label: "Profile", to: "/profile/settings", icon: UserRound },
];
const ADMIN_TAB: AppTab = { key: "admin", label: "Admin", to: "/admin", icon: Shield };

export function useAppTabs(): AppTab[] {
  const { playerAccount } = useAuth();
  return playerAccount?.is_admin ? [...BASE_TABS, ADMIN_TAB] : BASE_TABS;
}

/** Longest matching route prefix wins, so /home/stats is Stats and not Home. */
export function activeTabKey(pathname: string, tabs: AppTab[]): AppTab["key"] | null {
  const hits = tabs.filter((t) => pathname === t.to || pathname.startsWith(`${t.to}/`));
  hits.sort((a, b) => b.to.length - a.to.length);
  return hits[0]?.key ?? null;
}

export const AppTabs = ({ variant }: { variant: "top" | "bottom" }) => {
  const tabs = useAppTabs();
  const { pathname } = useLocation();
  const active = activeTabKey(pathname, tabs);
  const top = variant === "top";
  return (
    <nav aria-label="Main" className={top ? "flex h-full items-stretch gap-1" : "grid w-full"} style={top ? undefined : { gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
      {tabs.map(({ key, label, to, icon: Icon }) => {
        const on = key === active;
        return (
          <Link
            key={key}
            to={to}
            aria-current={on ? "page" : undefined}
            className={cn(
              top
                ? "flex items-center gap-2 border-b-2 px-3.5 text-[15px] font-medium"
                : "flex flex-col items-center gap-1 py-2 text-[11px] font-semibold",
              top && (on ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"),
              !top && (on ? "text-primary" : "text-muted-foreground"),
            )}
          >
            <Icon className={top ? "h-[18px] w-[18px]" : "h-[22px] w-[22px]"} aria-hidden />
            {label}
          </Link>
        );
      })}
    </nav>
  );
};
